import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

/**
 * Route guard for authenticated pages.
 *
 * It deliberately does NOT run its own `checkSession()`. `useAppInit` (via
 * `Observer`) resolves the Firebase session before `Routes` renders at all, so
 * by the time any route mounts `isLoggedIn` is already authoritative.
 *
 * The previous version dispatched `checkSession()` from an effect and returned
 * `null` while it was in flight. That painted the full layout + view on the
 * first render, unmounted the whole tree to a blank screen one tick later, then
 * remounted it -- a visible flash on every navigation, plus a duplicate of every
 * data fetch the view kicks off on mount.
 */
const AuthenticatedRoutes = (props) => {
  const {
    layout: Layout,
    component: Component,
    leftSidebar,
    competeSidebar,
    current,
    noFooter,
    noTopbar,
    ...rest
  } = props;
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  return (
    <Route
      {...rest}
      render={(matchProps) =>
        isLoggedIn ? (
          <Layout
            current={current}
            noTopbar={noTopbar}
            noFooter={noFooter}
            leftSidebar={leftSidebar}
            competeSidebar={competeSidebar}
          >
            <Component {...matchProps} />
          </Layout>
        ) : (
          <Redirect
            to={`/auth/signin?redirectToURI=${encodeURIComponent(
              location.pathname,
            )}`}
          />
        )
      }
    />
  );
};

AuthenticatedRoutes.propTypes = {
  component: PropTypes.any.isRequired,
  layout: PropTypes.any.isRequired,
  leftSidebar: PropTypes.bool,
  competeSidebar: PropTypes.bool,
  noFooter: PropTypes.bool,
  noTopbar: PropTypes.bool,
  current: PropTypes.string,
  path: PropTypes.string,
};

export default AuthenticatedRoutes;
